import LabelValue from '@app/components/common/LabelValue';
import {
  BILLING_METHOD_NEW_CC,
  BILLING_METHOD_BANK_TRANSFER,
  BILLING_METHOD_PRICING_LIMIT,
  LAST_FULFILLMENT_DATE_CHANGED,
  PICK_UP_TYPE,
  DROP_BOX_TYPE,
  DELIVERY_TYPE,
  FULFILMENT_DATE_CHANGED,
  LONG_DATE_FORMAT_WITH_TIME,
  BILLING_METHOD_EXISTING_CC,
  BILLING_METHOD_BLIK
} from '@app/constants/commonConstants';
import { getCookie, getLocalStorage, replaceTokensInString } from '@app/utils';
import { format, parse } from 'date-fns';
import {
  CLIENT_TIME_FORMAT,
  DELIVERY,
  DROPBOX,
  PICKUP,
  SHOP_ONLINE
} from '../checkout/PickUpAndDelivery/constants';
import { getAvailableByDate } from '../checkout/PickUpAndDelivery/utils';

export const isOtherPaymentType = method => {
  const { value = '' } = method || {};
  return (
    !value?.includes(BILLING_METHOD_NEW_CC) &&
    !value?.includes(BILLING_METHOD_BANK_TRANSFER) &&
    !value?.includes(BILLING_METHOD_BLIK)
  );
};

export const getEstimateDate = (
  fulfillmentType,
  pickUpRequestedDate,
  estimatedDropBoxDate,
  deliveryShippingMethods,
  shippingMethodCode,
  taxAndFreight,
  isGuest
) => {
  if (fulfillmentType === PICK_UP_TYPE && pickUpRequestedDate) {
    return pickUpRequestedDate;
  }
  if (fulfillmentType === DROP_BOX_TYPE && estimatedDropBoxDate) {
    return estimatedDropBoxDate;
  }

  if (fulfillmentType === DELIVERY_TYPE && deliveryShippingMethods) {
    return isGuest
      ? taxAndFreight
      : deliveryShippingMethods.find(fnd => fnd.code === shippingMethodCode)
          ?.estimatedDeliveryDate;
  }
};
export const isMicoNonDeliveryFlow = (micoEnabled, fulfillmentType) =>
  micoEnabled && getCookie('mico') && fulfillmentType !== 'delivery';

export const getToastMessage = (
  attachmentItems,
  attachmentRemovalFlag,
  value
) => {
  const count = attachmentItems.filter(item =>
    attachmentRemovalFlag ? item.attachmentRemovalFlag : item.file
  ).length;

  return count > 0 ? replaceTokensInString(value, count) : '';
};

export const getApproverInfo = (approvers, userId) => {
  return approvers?.find(approver => approver?.approverId === userId);
};

export const checkPriceLimitWarning = ({
  selectedBillingMethod,
  isOrderApprovalRequired
}) => {
  const isPriceLimitValid =
    !!selectedBillingMethod?.cardNumber ||
    BILLING_METHOD_PRICING_LIMIT.includes(selectedBillingMethod?.value);
  return isPriceLimitValid && isOrderApprovalRequired;
};

export const generateTaxAndFrightPayloadInSummaryPage = ({
  orderId,
  fullfillmentType,
  requestByDate,
  physicalStoreId,
  shipModeId,
  deliveryShipModeId,
  address,
  country,
  isGuestEnhancedEnabled = false,
  method,
  promoCodeApplied
}) => {
  return {
    orderId: orderId,
    fulfillmentType: fullfillmentType.toLowerCase(),
    requestByDate: requestByDate,
    promoCode: promoCodeApplied,
    isGuestEnhancedShippingOptionsEnabled: isGuestEnhancedEnabled,
    physicalStoreId: physicalStoreId || '',
    shipModeId: shipModeId || deliveryShipModeId || '',
    shippingMethod: method,
    shippingAddress: {
      firstName: address?.firstName,
      lastName: address?.lastName,
      nickName: address?.nickName,
      email: address?.email,
      phone: address?.phone,
      line1: address?.address1,
      line2: address?.address2,
      line3: address?.address3,
      country: country,
      city: address?.city,
      state: address?.state,
      zipCode: address?.zipCode || address?.zipcode || ''
    },
    clientTime: format(new Date(), CLIENT_TIME_FORMAT)
  };
};

export const getSelectedDate = (
  fulfillmentType,
  estimatedPickUpAvailableDate,
  estimatedDropBoxAvailableDate,
  estimatedDeliveryAvailableDate,
  defaultDate,
  rbdFormat
) => {
  const availableDate = getAvailableByDate(
    fulfillmentType,
    estimatedPickUpAvailableDate,
    estimatedDropBoxAvailableDate,
    estimatedDeliveryAvailableDate,
    rbdFormat
  );
  const lastDateModifiedFillment = getLocalStorage(
    LAST_FULFILLMENT_DATE_CHANGED
  );
  const fulfilmentDateChangedData = getLocalStorage(FULFILMENT_DATE_CHANGED);

  if (availableDate) {
    let formatDateAvailableDate = format(new Date(availableDate), rbdFormat);
    if (
      !!fulfilmentDateChangedData &&
      (fulfillmentType === SHOP_ONLINE ? DELIVERY : fulfillmentType) ===
        JSON.parse(fulfilmentDateChangedData).fulfillmentType
    ) {
      return {
        availableDate,
        formatDateAvailableDate: format(
          new Date(JSON.parse(fulfilmentDateChangedData).date),
          rbdFormat
        )
      };
    }
    if (
      lastDateModifiedFillment === null ||
      lastDateModifiedFillment?.toLowerCase() !== fulfillmentType.toLowerCase()
    ) {
      return { availableDate, formatDateAvailableDate };
    }
  }
  return { availableDate, formatDateAvailableDate: defaultDate };
};

export const getDeliveryShippingMethodDate = (
  isGuestUser,
  taxAndFreight,
  deliveryShippingMethods,
  defaultDate,
  selectedDeliveryMethodCode
) => {
  const deliveryShippingDate = deliveryShippingMethods?.find(
    fnd => fnd.code === selectedDeliveryMethodCode
  )?.estimatedDeliveryDate?.availableByDate;

  return isGuestUser
    ? (taxAndFreight?.find(
        tax => tax.shipViaCode === selectedDeliveryMethodCode
      )?.estimatedDeliveryDate?.availableByDate ?? defaultDate)
    : deliveryShippingDate;
};

export const isFulfillmentDateChanged = (
  formData,
  additionalData,
  requestByDate
) => {
  const {
    deliveryMethod: {
      estimatedDeliveryDate: { availableByDate: estDeliveryDt } = {}
    } = {},
    dropBox: {
      estimatedDropBoxDate: { availableByDate: estDropBoxDt } = {}
    } = {}
  } = formData;
  const { requestByDateFormatted, selectedFulfillmentMethod } = additionalData;
  const {
    estimatedPickupDate: { availableByDate: estPickupDt } = {},
    format: fmt
  } = requestByDate;

  if (
    estDeliveryDt &&
    (selectedFulfillmentMethod === SHOP_ONLINE ||
      selectedFulfillmentMethod === DELIVERY)
  ) {
    return (
      format(new Date(estDeliveryDt), fmt) !==
      format(new Date(requestByDateFormatted), fmt)
    );
  } else if (estDropBoxDt && selectedFulfillmentMethod === DROPBOX) {
    return (
      format(new Date(estDropBoxDt), fmt) !==
      format(new Date(requestByDateFormatted), fmt)
    );
  } else if (
    (selectedFulfillmentMethod === PICKUP ||
      selectedFulfillmentMethod === PICK_UP_TYPE) &&
    estPickupDt
  ) {
    return (
      format(new Date(estPickupDt), fmt) !==
      format(new Date(requestByDateFormatted), fmt)
    );
  }
};

export const getNumericPrice = ({ priceInString }) =>
  +priceInString?.replace(/[^0-9.]/g, '');

export const isInlineForm = ({
  isPendingOrder,
  cstoAddresses,
  billingAddresses,
  latestBillingMethod,
  micoEnabled,
  fulfillmentType
}) =>
  !isPendingOrder &&
  ((!cstoAddresses.length && !billingAddresses.length) ||
    (!isOtherPaymentType(latestBillingMethod) && !billingAddresses.length) ||
    isMicoNonDeliveryFlow(micoEnabled, fulfillmentType));

export const evaluateRegexExpression = ({ value, regx }) =>
  value && value.match(regx);

export const shouldRenderAdditionalPaymentOptionsEvaluate = ({
  isPendingOrder,
  selectedBillingMethod,
  catCreditsConst,
  remainingOrderTotal,
  certificates
}) =>
  !isPendingOrder &&
  selectedBillingMethod?.value === catCreditsConst &&
  !!remainingOrderTotal &&
  remainingOrderTotal !== '0' &&
  certificates?.length > 0;

export const formatedOrderPlacedDate = date =>
  date && format(new Date(date), LONG_DATE_FORMAT_WITH_TIME);

export const getAllItemField = allItemField => {
  return allItemField.map(
    ({ title, value, checkCondition, children, ...rest }) => {
      if (!checkCondition) {
        return null;
      }
      return children || <LabelValue title={title} value={value} {...rest} />;
    }
  );
};

export const getInitialAvailableDate = (
  formatDateAvailableDate,
  parsedDate,
  rbdFormat
) =>
  !!formatDateAvailableDate
    ? parse(formatDateAvailableDate, rbdFormat, new Date())
    : parsedDate;

export const getPaymentSummaryHeading = (
  t,
  isPCCPaymentAndSummaryTitleUpdates
) => {
  return isPCCPaymentAndSummaryTitleUpdates
    ? t('PAYMENT_AND_SUMMARY')
    : t('SUMMARY_PAYMENT_PAGE');
};

export const isPunchoutBillingMethod = method =>
  [
    BILLING_METHOD_EXISTING_CC,
    BILLING_METHOD_NEW_CC,
    BILLING_METHOD_BANK_TRANSFER
  ].includes(method);
